import { Options, Vue } from 'vue-class-component';
import PageTitleComponent from '@/sharedcomponents/pageComponents/pageTitle/PageTitleComponent.vue';
import { ReceiptPage, Signee, UsabillaInPageBlock } from '@/cms/definitions/content-types';
import store from '@/store/store';
import CookieFooter from '@/sharedcomponents/pageComponents/cookieFooter/cookieFooter.vue';
import VerticalStepComponent from './VerticalStepComponent.vue';
import UsabillaInPageComponent from '@/sharedcomponents/pageComponents/usabillaInPageComponent/UsabillaInPageComponent.vue';
import ModelMapper from './ModelMapper';
import { getDomain, handleIdleLogout, isDevelop } from '@/services/environmentService';
import { mapState } from 'vuex';
import { Log } from '@/appinsights/logging/log';
import { nextTick, PropType } from 'vue';
import { loadProposal, resolvePageLoadingError } from '@/services/PageService';
import { trackError, trackSignicat } from '@/services/TrackService';

@Options({
	name: 'ReceiptComponent',
	components: {
		PageTitleComponent,
		VerticalStepComponent,
		UsabillaInPageComponent,
		CookieFooter,
	},
	computed: mapState<any>({
		hasError: (state) => store.state.hasError,
		mockedDataLoaded: (state) => store.state.mockedDataLoaded,
	}),
	props: {
		model: Object as PropType<ReceiptPage>,
	},
})
export default class ReceiptComponent extends Vue {
	public model!: ReceiptPage; // epi contentModel
	public usabillaBlock: UsabillaInPageBlock = null; // epi contentModel
	public renderPage = false;
	public headline = '...';
	public policy: any;
	public proposalIdLabel: string;
	public proposalId: string;
	public isDevelop = isDevelop;

	public domain = getDomain();
	public loadError = null;
	public allSignaturesDone = false;

	public mockedDataLoaded!: boolean;

	public beforeCreate() {
		handleIdleLogout(this.domain.url, 5);
	}

	public beforeMount() {
		this.init();
	}

	private async init() {
		store.state.mockedDataLoaded = false;
		store.state.showSpinner = true;
		let proposal;
		try {
			proposal = await loadProposal();

			this.handleSignatures(proposal);

			this.headline = this.allSignaturesDone ? this.model.headline : this.model.headlineMissingSignature;

			store.state.ProposalContext.originalRequest = proposal.data;
			const modelMapper: ModelMapper = await ModelMapper.factory(proposal, this.model);
			this.policy = modelMapper.getReceiptPolicyDetails();
			this.proposalIdLabel = this.model.offerIdLabel;
			this.proposalId = store.state.ProposalContext.quote_id;

			trackSignicat(false);

			this.setupUsabilla();

			if (isDevelop && store.state.mockedDataLoaded) {
				// fake wait
				setTimeout(() => {
					this.renderPage = true;
					store.state.showSpinner = false;
				}, 2500);
			} else {
				this.renderPage = true;
				store.state.showSpinner = false;
			}
		} catch (error) {
			Log.exception('Error mounting receipt', error, { quoteId: store.state.ProposalContext.quote_id });

			store.state.showSpinner = false;
			nextTick(() => {
				this.loadError = resolvePageLoadingError(proposal?.data?.type);
			});
			trackError('ReceiptLoadError', error.message);
		}
	}

	// Race condition: If there is only one signee we can assume that all signatures are done, as the policy is not yet updated when the first signee has signed.
	// Race condition: If there is 2 signees and one is true, we can assume that all signatures are true, as the policy is not yet updated.
	private handleSignatures(proposal) {
		const signees: Signee[] = proposal?.data?.signees;
		this.allSignaturesDone = true;
		if (signees?.length === 2) {
			this.allSignaturesDone = false;
			for (const signee of signees) {
				if (signee.signed) {
					this.allSignaturesDone = true;
					return;
				}
			}
		}
	}

	public get subtitle() {
		let subtitle = this.model.subTitle;
		if (!this.allSignaturesDone) {
			subtitle = this.model.subTitleMissingSignature;
		}

		if (!subtitle) {
			return undefined;
		}
		return subtitle.trim() === '' ? undefined : subtitle;
	}

	private setupUsabilla(): void {
		const items = this.model.contentArea?.items;
		if (items?.length > 0) {
			items.forEach((block) => {
				if (block?.content.contentType === 'UsabillaInPageBlock') {
					this.usabillaBlock = block.content;
				}
			});
		}
	}

	public get showSpinner() {
		return store.state.showSpinner;
	}

	public nextStep() {
		store.state.showSpinner = true;
		location.href = getDomain().url;
	}
}
